import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useAdminNpcs } from '../hooks/useAdminNpcs';
import { useAdminTasks } from '../hooks/useAdminTasks';
import { useAdminLevels } from '../hooks/useAdminLevels';
import {
  TaskFormFields,
  EMPTY_TASK_FORM_STATE,
  TaskFormState,
  getDescendantTaskIds,
  parseTaskFormContent,
  serializeContentToJson,
} from '../components/TaskFormFields';

export function AdminEditTaskPage() {
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();
  const { npcs, loading: npcsLoading } = useAdminNpcs();
  const { tasks: existingTasks, loading: tasksLoading } = useAdminTasks();
  const { levels, loading: levelsLoading } = useAdminLevels();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<TaskFormState>(EMPTY_TASK_FORM_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await api.adminGetLitLegionTask(taskId);
        if (cancelled) return;
        const t = result.task;
        setForm({
          name: t.name,
          description: t.description,
          taskType: t.taskType,
          category: t.category ?? '',
          xpReward: t.xpReward,
          npcId: t.npcId ?? 'none',
          parentId: t.parentId ?? 'none',
          minLevelId: t.minLevelId ?? 'none',
          npcTextIntro: t.npcTextIntro ?? '',
          npcTextObjectives: t.npcTextObjectives ?? '',
          npcTextInProgress: t.npcTextInProgress ?? '',
          npcTextSuccess: t.npcTextSuccess ?? '',
          coinRewards: result.coinRewards.map((r) => ({
            coinId: r.coinId,
            coinName: r.coinName,
            quantity: r.quantity,
          })),
          badgeRewards: result.badgeRewards.map((r) => ({ badgeId: r.badgeId })),
          definitionJson: serializeContentToJson(t.definition),
          privateDefinitionJson: serializeContentToJson(t.privateDefinition),
        });
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Failed to load task');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId]);

  // Exclude self + descendants from parent options to prevent UI-introduced
  // cycles. The server enforces this too; this is a friendlier UX.
  const parentTaskOptions = useMemo(() => {
    if (!taskId) return existingTasks;
    const blocked = getDescendantTaskIds(taskId, existingTasks);
    blocked.add(taskId);
    return existingTasks.filter((t) => !blocked.has(t.taskId));
  }, [existingTasks, taskId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!taskId) return;

    if (!form.name.trim()) {
      setError('Task name is required');
      return;
    }

    const content = parseTaskFormContent(form);
    if (content.parseError) {
      setError(content.parseError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.adminUpdateLitLegionTask(taskId, {
        name: form.name.trim(),
        description: form.description.trim(),
        taskType: form.taskType,
        xpReward: form.xpReward,
        category: form.category.trim() || null,
        npcId: form.npcId === 'none' ? null : form.npcId,
        parentId: form.parentId === 'none' ? null : form.parentId,
        minLevelId: form.minLevelId === 'none' ? null : form.minLevelId,
        npcTextIntro: form.npcTextIntro,
        npcTextObjectives: form.npcTextObjectives,
        npcTextInProgress: form.npcTextInProgress,
        npcTextSuccess: form.npcTextSuccess,
        definition: content.definition,
        privateDefinition: content.privateDefinition,
        coinRewards: form.coinRewards.filter((c) => c.coinId.trim()),
        badgeRewards: form.badgeRewards.filter((b) => b.badgeId.trim()),
      });
      navigate('/a/arche_lit_legion/admin/tasks');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <Link
          to="/a/arche_lit_legion/admin/tasks"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Task List
        </Link>
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{loadError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin/tasks"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Task List
        </Link>
        <h1 className="text-2xl font-semibold">Edit Task</h1>
        <p className="text-sm text-muted-foreground mt-1">Update task details and rewards</p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <TaskFormFields
          value={form}
          onChange={setForm}
          parentTaskOptions={parentTaskOptions}
          parentTasksLoading={tasksLoading}
          npcs={npcs}
          npcsLoading={npcsLoading}
          levels={levels}
          levelsLoading={levelsLoading}
        />

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/a/arche_lit_legion/admin/tasks')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
